import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { FiX, FiSearch, FiChevronDown, FiFileText, FiCreditCard } from 'react-icons/fi';
import toast from 'react-hot-toast';
import ParcelasModal from './ParcelasModal';
import { FORMAS_PAGAMENTO, formaIndicaCarne } from '../constants/formasPagamento';

const PassageiroModal = ({ passageiro, viagens, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nome: '',
    endereco: '',
    bairro: '',
    cidade: '',
    telefone: '',
    rg: '',
    cpf: '',
    email: '',
    viagem_id: '',
    assento_id: '',
    status: 'ativo',
    forma_pagamento: '',
    numero_parcelas: '',
    valor_sinal: '',
    valor_passagem: '',
    status_pagamento: 'pendente',
    promocao_recebida: false,
    promocao_confirmada: false,
    promocao_viagem_id: ''
  });
  const [assentos, setAssentos] = useState([]);
  const [viagemSelecionada, setViagemSelecionada] = useState(null);
  const [loadingAssentos, setLoadingAssentos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buscaCPF, setBuscaCPF] = useState('');
  const [buscandoPassageiro, setBuscandoPassageiro] = useState(false);
  const [historicoViagens, setHistoricoViagens] = useState([]);
  const [assentoDropdownOpen, setAssentoDropdownOpen] = useState(false);
  const assentoDropdownRef = useRef(null);
  const [parcelasModalOpen, setParcelasModalOpen] = useState(false);

  const formatarCPF = (cpf) => {
    const cpfLimpo = cpf.replace(/\D/g, '');
    return cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const buscarPassageiroPorCPF = async () => {
    const cpfLimpo = buscaCPF.replace(/\D/g, '');
    
    if (!cpfLimpo || cpfLimpo.length < 11) {
      toast.error('Digite um CPF válido com 11 dígitos');
      return;
    }

    setBuscandoPassageiro(true);
    try {
      const cpfEncoded = encodeURIComponent(cpfLimpo);
      const response = await api.get(`/api/passageiros/buscar/cpf/${cpfEncoded}`);
      const passageiroEncontrado = response.data;

      // Preencher os dados do formulário
      setFormData({
        ...formData,
        nome: passageiroEncontrado.nome || formData.nome,
        endereco: passageiroEncontrado.endereco || formData.endereco,
        bairro: passageiroEncontrado.bairro || formData.bairro,
        cidade: passageiroEncontrado.cidade || formData.cidade,
        telefone: passageiroEncontrado.telefone || formData.telefone,
        rg: passageiroEncontrado.rg || formData.rg,
        cpf: passageiroEncontrado.cpf || formData.cpf,
        email: passageiroEncontrado.email || formData.email,
        // Não preencher viagem_id automaticamente, deixa o usuário escolher
        status: passageiroEncontrado.status || formData.status,
      });
      
      // Limpar campo de busca após encontrar
      setBuscaCPF('');

      // Armazenar histórico de viagens
      if (passageiroEncontrado.historico_viagens) {
        setHistoricoViagens(passageiroEncontrado.historico_viagens);
        if (passageiroEncontrado.historico_viagens.length > 0) {
          toast.success(`Passageiro encontrado! Já viajou ${passageiroEncontrado.historico_viagens.length} vez(es) conosco.`);
        } else {
          toast.success('Passageiro encontrado! Dados preenchidos automaticamente.');
        }
      } else {
        toast.success('Passageiro encontrado! Dados preenchidos automaticamente.');
      }
    } catch (error) {
      console.error('Erro ao buscar passageiro:', error);
      if (error.response?.status === 404) {
        toast.error('Passageiro não encontrado com este CPF. Verifique se o CPF está correto ou continue preenchendo os dados manualmente.');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.error || 'CPF inválido. Digite um CPF com 11 dígitos.');
      } else {
        toast.error(error.response?.data?.error || 'Erro ao buscar passageiro. Tente novamente.');
      }
    } finally {
      setBuscandoPassageiro(false);
    }
  };

  useEffect(() => {
    if (passageiro) {
      // Extrair forma de pagamento e informações de parcelamento das observações
      let formaPagamento = passageiro.forma_pagamento || '';
      let numeroParcelas = '';
      let valorSinal = '';
      
      if (formaPagamento && formaPagamento.includes('Forma de pagamento: ')) {
        formaPagamento = formaPagamento.replace('Forma de pagamento: ', '');
      }
      
      // Extrair informações de parcelamento se existirem
      if (passageiro.forma_pagamento) {
        const parcelasMatch = passageiro.forma_pagamento.match(/Parcelas: (\d+)/i);
        const sinalMatch = passageiro.forma_pagamento.match(/Sinal: R\$\s?([\d,\.]+)/i);
        
        if (parcelasMatch) {
          numeroParcelas = parcelasMatch[1];
        }
        if (sinalMatch) {
          valorSinal = sinalMatch[1].replace(',', '.');
        }
      }

      setFormData({
        nome: passageiro.nome || '',
        endereco: passageiro.endereco || '',
        bairro: passageiro.bairro || '',
        cidade: passageiro.cidade || '',
        telefone: passageiro.telefone || '',
        rg: passageiro.rg || '',
        cpf: passageiro.cpf || '',
        email: passageiro.email || '',
        viagem_id: passageiro.viagem_id || '',
        assento_id: passageiro.assento_id || '',
        status: passageiro.status || 'ativo',
        forma_pagamento: formaPagamento,
        numero_parcelas: numeroParcelas,
        valor_sinal: valorSinal,
        valor_passagem: passageiro.valor_passagem || '',
        status_pagamento: passageiro.status_pagamento || 'pendente',
        promocao_recebida: passageiro.promocao_recebida === 1 || false,
        promocao_confirmada: passageiro.promocao_confirmada === 1 || false,
        promocao_viagem_id: passageiro.promocao_viagem_id || ''
      });
      // Carregar assento atual se houver viagem
      if (passageiro.viagem_id) {
        fetchAssentos(passageiro.viagem_id, passageiro.id);
        // Carregar dados da viagem para pegar o valor padrão
        const viagem = viagens.find(v => v.id === passageiro.viagem_id);
        if (viagem) {
          setViagemSelecionada(viagem);
          if (!passageiro.valor_passagem) {
            setFormData(prev => ({ ...prev, valor_passagem: viagem.valor || '' }));
          }
        }
      }
      // Limpar busca e histórico quando estiver editando
      setBuscaCPF('');
      setHistoricoViagens([]);
    } else {
      // Limpar busca e histórico ao criar novo passageiro
      setBuscaCPF('');
      setHistoricoViagens([]);
    }
  }, [passageiro, viagens]);

  const fetchAssentos = async (viagemId, passageiroId = null) => {
    if (!viagemId) {
      setAssentos([]);
      return;
    }

    setLoadingAssentos(true);
    try {
      const response = await api.get(`/api/assentos/viagem/${viagemId}`);
      // Mostrar TODOS os assentos para visualização com cores
      // Ordenar por fileira e número
      const assentosOrdenados = response.data.sort((a, b) => {
        if (a.fileira !== b.fileira) {
          return a.fileira.localeCompare(b.fileira);
        }
        return a.numero - b.numero;
      });
      setAssentos(assentosOrdenados);
      
      // Se estiver editando e o passageiro já tiver assento, selecionar automaticamente
      if (passageiroId) {
        const assentoAtual = response.data.find(a => a.passageiro_id === passageiroId);
        if (assentoAtual) {
          setFormData(prev => ({ ...prev, assento_id: assentoAtual.id }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar assentos:', error);
      setAssentos([]);
    } finally {
      setLoadingAssentos(false);
    }
  };

  useEffect(() => {
    if (formData.viagem_id) {
      fetchAssentos(formData.viagem_id, passageiro?.id);
      // Carregar dados da viagem para pegar o valor padrão
      const viagem = viagens.find(v => v.id === formData.viagem_id);
      if (viagem) {
        setViagemSelecionada(viagem);
        if (!formData.valor_passagem || formData.valor_passagem === '') {
          setFormData(prev => ({ ...prev, valor_passagem: viagem.valor || '' }));
        }
      }
    } else {
      setAssentos([]);
      setViagemSelecionada(null);
      setFormData(prev => ({ ...prev, assento_id: '', valor_passagem: '' }));
    }
  }, [formData.viagem_id]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assentoDropdownRef.current && !assentoDropdownRef.current.contains(event.target)) {
        setAssentoDropdownOpen(false);
      }
    };

    if (assentoDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [assentoDropdownOpen]);

  const handleGerarComprovante = async () => {
    if (!passageiro || !passageiro.id) {
      toast.error('Salve o passageiro primeiro para gerar o comprovante');
      return;
    }

    try {
      // Obter token de autenticação
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Você precisa estar autenticado para gerar o comprovante');
        return;
      }

      const response = await api.get(`/api/passageiros/${passageiro.id}/comprovante`, {
        responseType: 'blob',
      });

      // Criar blob e abrir em nova aba
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = `comprovante_${passageiro.nome.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Comprovante gerado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar comprovante');
      console.error(error);
    }
  };

  const handleGerarContrato = async () => {
    if (!passageiro || !passageiro.id) {
      toast.error('Salve o passageiro primeiro para gerar o contrato');
      return;
    }

    try {
      const response = await api.get(`/api/passageiros/${passageiro.id}/contrato`, {
        responseType: 'blob'
      });

      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_${passageiro.nome.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Contrato gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar contrato:', error);
      toast.error(error.response?.data?.error || 'Erro ao gerar contrato');
    }
  };

  const handleEnviarWhatsApp = async () => {
    if (!passageiro || !passageiro.id) {
      toast.error('Salve o passageiro primeiro para enviar o comprovante');
      return;
    }

    if (!formData.telefone) {
      toast.error('Passageiro não possui telefone cadastrado');
      return;
    }

    try {
      const response = await api.post(`/api/passageiros/${passageiro.id}/enviar-comprovante`);
      if (response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
        toast.success('WhatsApp aberto! O comprovante será enviado ao passageiro.');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar link do WhatsApp');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let passageiroId;
      const assentoAnteriorId = passageiro?.assento_id;

      if (passageiro) {
        await api.put(`/api/passageiros/${passageiro.id}`, formData);
        passageiroId = passageiro.id;
        toast.success('Passageiro atualizado com sucesso');
      } else {
        const response = await api.post('/api/passageiros', formData);
        passageiroId = response.data.id;
        toast.success('Passageiro criado com sucesso');
      }

      // Gerenciar assentos
      if (formData.viagem_id && passageiroId) {
        // Se estiver editando um passageiro existente
        if (passageiro) {
          const novoAssentoId = formData.assento_id;

          // Caso 1: passageiro já tinha assento e mudou para outro
          if (assentoAnteriorId && novoAssentoId && assentoAnteriorId !== novoAssentoId) {
            try {
              await api.post('/api/assentos/liberar', {
                assento_id: assentoAnteriorId
              });
            } catch (error) {
              console.error('Erro ao liberar assento anterior:', error);
            }

            try {
              await api.post('/api/assentos/reservar', {
                viagem_id: formData.viagem_id,
                assento_id: novoAssentoId,
                passageiro_id: passageiroId
              });
            } catch (error) {
              if (error.response?.data?.error?.includes('ocupado')) {
                toast.error('Assento já está ocupado. Selecione outro assento.');
                setLoading(false);
                return;
              }
              console.error('Erro ao reservar assento:', error);
            }
          }
          // Caso 2: passageiro não tinha assento e agora ganhou um
          else if (!assentoAnteriorId && novoAssentoId) {
            try {
              await api.post('/api/assentos/reservar', {
                viagem_id: formData.viagem_id,
                assento_id: novoAssentoId,
                passageiro_id: passageiroId
              });
            } catch (error) {
              if (error.response?.data?.error?.includes('ocupado')) {
                toast.error('Assento já está ocupado. Selecione outro assento.');
                setLoading(false);
                return;
              }
              console.error('Erro ao reservar assento:', error);
            }
          }
          // Caso 3: remover assento (sem novo selecionado)
          else if (!novoAssentoId && assentoAnteriorId) {
            try {
              await api.post('/api/assentos/liberar', {
                assento_id: assentoAnteriorId
              });
            } catch (error) {
              console.error('Erro ao liberar assento:', error);
            }
          }
          // Caso 4: assento não mudou -> não faz nada
        } else {
          // Se for um novo passageiro, reservar o assento normalmente
          if (formData.assento_id) {
            try {
              await api.post('/api/assentos/reservar', {
                viagem_id: formData.viagem_id,
                assento_id: formData.assento_id,
                passageiro_id: passageiroId
              });
            } catch (error) {
              if (error.response?.data?.error?.includes('ocupado')) {
                toast.error('Assento já está ocupado. Selecione outro assento.');
                setLoading(false);
                return;
              }
              console.error('Erro ao reservar assento:', error);
            }
          }
        }

        // Gerenciar pagamento (só se houver viagem selecionada)
        if (formData.viagem_id && formData.valor_passagem) {
          try {
            // Verificar se já existe pagamento para este passageiro e viagem
            const pagamentosRes = await api.get(`/api/pagamentos?passageiro_id=${passageiroId}&viagem_id=${formData.viagem_id}`);
            const pagamentoExistente = pagamentosRes.data[0];

            // Montar observações com forma de pagamento e informações de parcelamento
            let observacoes = '';
            if (formData.forma_pagamento) {
              observacoes = `Forma de pagamento: ${formData.forma_pagamento}`;
              
              if (formaIndicaCarne(formData.forma_pagamento) && formData.numero_parcelas) {
                observacoes += ` | Parcelas: ${formData.numero_parcelas}x`;
              }
              
              if (formData.forma_pagamento === 'Sinal' && formData.valor_sinal) {
                observacoes += ` | Sinal: R$ ${parseFloat(formData.valor_sinal).toFixed(2)}`;
              }
            }

            const dadosPagamento = {
              passageiro_id: passageiroId,
              viagem_id: formData.viagem_id,
              valor: parseFloat(formData.valor_passagem),
              status: formData.status_pagamento,
              data_pagamento: formData.status_pagamento === 'pago' ? new Date().toISOString().split('T')[0] : null,
              observacoes: observacoes || null
            };

            let pagamentoCriadoOuAtualizado = false;
            let pagamentoId = null;
            
            if (pagamentoExistente) {
              // Atualizar pagamento existente
              await api.put(`/api/pagamentos/${pagamentoExistente.id}`, dadosPagamento);
              pagamentoCriadoOuAtualizado = true;
              pagamentoId = pagamentoExistente.id;
            } else {
              // Criar novo pagamento
              const response = await api.post('/api/pagamentos', dadosPagamento);
              pagamentoCriadoOuAtualizado = true;
              pagamentoId = response.data.id;
            }

            // Se for pagamento em carnê, criar parcelas automaticamente
            if (pagamentoId && formaIndicaCarne(formData.forma_pagamento) && formData.numero_parcelas) {
              try {
                await api.post('/api/parcelas/criar-parcelas', {
                  pagamento_id: pagamentoId,
                  numero_parcelas: parseInt(formData.numero_parcelas),
                  valor_total: parseFloat(formData.valor_passagem),
                  data_primeiro_vencimento: new Date().toISOString().split('T')[0]
                });
                toast.success(`${formData.numero_parcelas} parcelas criadas automaticamente!`);
              } catch (error) {
                console.error('Erro ao criar parcelas:', error);
                // Não bloquear o fluxo se houver erro na criação de parcelas
              }
            }
          } catch (error) {
            console.error('Erro ao salvar pagamento:', error);
            // Não bloquear o salvamento do passageiro se houver erro no pagamento
          }
        }
      }
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar passageiro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {passageiro ? 'Editar Passageiro' : 'Novo Passageiro'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Campo de busca - só aparece quando está criando novo passageiro */}
          {!passageiro && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar Passageiro por CPF
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={buscaCPF}
                  onChange={(e) => {
                    let valor = e.target.value.replace(/\D/g, '');
                    if (valor.length <= 11) {
                      // Formatar CPF enquanto digita
                      if (valor.length > 3) {
                        valor = valor.replace(/(\d{3})(\d)/, '$1.$2');
                      }
                      if (valor.length > 7) {
                        valor = valor.replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3');
                      }
                      if (valor.length > 11) {
                        valor = valor.replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
                      }
                      setBuscaCPF(valor);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      buscarPassageiroPorCPF();
                    }
                  }}
                  placeholder="000.000.000-00"
                  maxLength={14}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={buscarPassageiroPorCPF}
                  disabled={buscandoPassageiro || !buscaCPF.replace(/\D/g, '').trim() || buscaCPF.replace(/\D/g, '').length < 11}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FiSearch size={18} />
                  {buscandoPassageiro ? 'Buscando...' : 'Buscar'}
                </button>
              </div>
              {historicoViagens.length > 0 && (
                <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Histórico de Viagens:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {historicoViagens.map((v, idx) => (
                      <li key={idx}>• {v.titulo} - {v.destino} {v.data_ida ? `(${new Date(v.data_ida + 'T00:00:00').toLocaleDateString('pt-BR')})` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
              <input
                type="text"
                value={formData.endereco}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Rua, número..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
              <input
                type="text"
                value={formData.bairro}
                onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
              <input
                type="text"
                value={formData.cidade}
                onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <input
                type="text"
                value={formData.telefone}
                onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RG</label>
              <input
                type="text"
                value={formData.rg}
                onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
              <input
                type="text"
                value={formData.cpf}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Viagem</label>
              <select
                value={formData.viagem_id}
                onChange={(e) => setFormData({ ...formData, viagem_id: e.target.value, assento_id: '' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Selecione uma viagem</option>
                {viagens.map((v) => (
                  <option key={v.id} value={v.id}>{v.titulo}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Poltrona {loadingAssentos && <span className="text-xs text-gray-500">(carregando...)</span>}
              </label>
              {!formData.viagem_id ? (
                <select
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                >
                  <option value="">Selecione uma viagem primeiro</option>
                </select>
              ) : (
                <div className="relative" ref={assentoDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setAssentoDropdownOpen(!assentoDropdownOpen)}
                    disabled={loadingAssentos}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-left flex items-center justify-between bg-white"
                  >
                    <span className={formData.assento_id ? 'text-gray-900' : 'text-gray-500'}>
                      {formData.assento_id 
                        ? (() => {
                            const assentoSelecionado = assentos.find(a => a.id === formData.assento_id);
                            if (assentoSelecionado) {
                              // Status visual baseado no status real do assento
                              let statusLabel = 'Disponível';
                              if (assentoSelecionado.status === 'ocupado') statusLabel = 'Ocupado';
                              else if (assentoSelecionado.status === 'reservado') statusLabel = 'Reservado';
                              return `Poltrona ${assentoSelecionado.numero} - ${statusLabel}`;
                            }
                            return 'Selecione uma poltrona';
                          })()
                        : 'Selecione uma poltrona (opcional)'}
                    </span>
                    <FiChevronDown className={`text-gray-400 transition-transform ${assentoDropdownOpen ? 'transform rotate-180' : ''}`} />
                  </button>
                  
                  {assentoDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div className="p-2">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, assento_id: '' });
                            setAssentoDropdownOpen(false);
                          }}
                          className="w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-100 rounded"
                        >
                          Nenhuma poltrona (opcional)
                        </button>
                      </div>
                      {assentos.map((assento) => {
                        // Para desabilitar clique, só bloqueia se estiver ocupado por outro passageiro
                        const isOcupadoPorOutro = assento.status === 'ocupado' && assento.passageiro_id !== passageiro?.id;
                        
                        // Cores/legenda baseadas no status real do assento
                        const status = assento.status;
                        const isOcupadoVisual = status === 'ocupado';
                        const isReservadoVisual = status === 'reservado';
                        
                        const bgColor = isOcupadoVisual ? 'bg-red-50 border-red-300' : isReservadoVisual ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300';
                        const textColor = isOcupadoVisual ? 'text-red-800' : isReservadoVisual ? 'text-yellow-800' : 'text-green-800';
                        const borderColor = isOcupadoVisual ? 'border-red-400' : isReservadoVisual ? 'border-yellow-400' : 'border-green-400';
                        
                        return (
                          <div
                            key={assento.id}
                            className={`p-2 border-l-4 ${bgColor} ${borderColor}`}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                if (!isOcupadoPorOutro) {
                                  setFormData({ ...formData, assento_id: assento.id });
                                  setAssentoDropdownOpen(false);
                                }
                              }}
                              disabled={isOcupadoPorOutro}
                              className={`w-full text-left px-3 py-2 rounded ${textColor} ${isOcupadoPorOutro ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white hover:shadow-sm cursor-pointer'}`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">
                                  Poltrona {assento.numero}
                                </span>
                                <span className="text-xs">
                                  {isOcupadoVisual ? 'Ocupado' : isReservadoVisual ? 'Reservado' : 'Disponível'}
                                </span>
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {formData.viagem_id && (
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-green-200 border border-green-400"></div>
                    <span className="text-gray-600">Disponível</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-yellow-200 border border-yellow-400"></div>
                    <span className="text-gray-600">Reservado</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-red-200 border border-red-400"></div>
                    <span className="text-gray-600">Ocupado</span>
                  </div>
                </div>
              )}
              {assentos.length === 0 && !loadingAssentos && formData.viagem_id && (
                <p className="text-xs text-gray-500 mt-1">Nenhum assento disponível para esta viagem</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
          </div>

          {/* Seção de Pagamento */}
          <div className="pt-4 border-t">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações de Pagamento</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor da Passagem (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.valor_passagem}
                  onChange={(e) => setFormData({ ...formData, valor_passagem: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder={viagemSelecionada ? `Valor padrão: R$ ${parseFloat(viagemSelecionada.valor || 0).toFixed(2)}` : 'Digite o valor'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
                <select
                  value={formData.forma_pagamento}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData({ 
                      ...formData, 
                      forma_pagamento: v,
                      // Limpar campos relacionados quando mudar forma de pagamento
                      numero_parcelas: !formaIndicaCarne(v) ? '' : formData.numero_parcelas,
                      valor_sinal: e.target.value !== 'Sinal' ? '' : formData.valor_sinal
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Selecione...</option>
                  {FORMAS_PAGAMENTO.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status do Pagamento</label>
                <select
                  value={formData.status_pagamento}
                  onChange={(e) => setFormData({ ...formData, status_pagamento: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="pendente">Pendente</option>
                  <option value="pago">Pago</option>
                </select>
              </div>
            </div>
            
            {/* Campos condicionais de parcelamento e sinal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Campo de parcelas - aparece apenas quando for Carnê */}
              {formaIndicaCarne(formData.forma_pagamento) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de Parcelas (até 10x)
                  </label>
                  <select
                    value={formData.numero_parcelas}
                    onChange={(e) => setFormData({ ...formData, numero_parcelas: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Selecione...</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num}x</option>
                    ))}
                  </select>
                  {formData.numero_parcelas && formData.valor_passagem && (
                    <p className="text-xs text-gray-500 mt-1">
                      Valor por parcela: R$ {((parseFloat(formData.valor_passagem) || 0) / parseInt(formData.numero_parcelas)).toFixed(2)}
                    </p>
                  )}
                </div>
              )}
              
              {/* Campo de sinal - aparece apenas quando for Sinal */}
              {formData.forma_pagamento === 'Sinal' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor do Sinal (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor_sinal}
                    onChange={(e) => setFormData({ ...formData, valor_sinal: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Digite o valor do sinal"
                  />
                  {formData.valor_sinal && formData.valor_passagem && (
                    <p className="text-xs text-gray-500 mt-1">
                      Restante: R$ {(parseFloat(formData.valor_passagem) - parseFloat(formData.valor_sinal || 0)).toFixed(2)}
                    </p>
                  )}
                </div>
              )}
            </div>
            {!formData.viagem_id && (
              <p className="text-xs text-gray-500 mt-2">
                * Selecione uma viagem para vincular o pagamento automaticamente
              </p>
            )}
          </div>

          {/* Seção de Promoção */}
          <div className="pt-4 border-t">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Promoção</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="promocao_recebida"
                  checked={formData.promocao_recebida}
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      promocao_recebida: e.target.checked,
                      // Se desmarcar "recebida", também desmarcar "confirmada"
                      promocao_confirmada: e.target.checked ? formData.promocao_confirmada : false
                    });
                  }}
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="promocao_recebida" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Passageiro recebeu promoção
                </label>
              </div>
              
              {formData.promocao_recebida && (
                <div className="ml-8 space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="promocao_confirmada"
                      checked={formData.promocao_confirmada}
                      onChange={(e) => setFormData({ ...formData, promocao_confirmada: e.target.checked })}
                      className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label htmlFor="promocao_confirmada" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Passageiro confirmou a promoção
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Viagem da Promoção
                    </label>
                    <select
                      value={formData.promocao_viagem_id}
                      onChange={(e) => setFormData({ ...formData, promocao_viagem_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Selecione a viagem promocional</option>
                      {viagens.map((v) => (
                        <option key={v.id} value={v.id}>{v.titulo}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Selecione a viagem para a qual o passageiro recebeu a promoção
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            {/* Botões de ação do comprovante - só aparecem quando está editando */}
            {passageiro && passageiro.id && (
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleGerarComprovante}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  title="Gerar comprovante em PDF"
                >
                  <FiFileText size={18} />
                  Gerar Comprovante
                </button>
                <button
                  type="button"
                  onClick={handleGerarContrato}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  title="Gerar contrato em PDF"
                >
                  <FiFileText size={18} />
                  Gerar Contrato
                </button>
                {formData.telefone && (
                  <button
                    type="button"
                    onClick={handleEnviarWhatsApp}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    title="Enviar comprovante via WhatsApp"
                  >
                    📱 Enviar via WhatsApp
                  </button>
                )}
                {/* Botão para gerenciar parcelas - só aparece se for pagamento em carnê */}
                {formaIndicaCarne(formData.forma_pagamento) && (
                  <button
                    type="button"
                    onClick={() => setParcelasModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    title="Gerenciar parcelas do carnê"
                  >
                    <FiCreditCard size={18} />
                    Gerenciar Parcelas
                  </button>
                )}
              </div>
            )}
            
            {/* Botões de ação do formulário */}
            <div className="flex gap-4 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Modal de Parcelas */}
      {parcelasModalOpen && passageiro && passageiro.id && (
        <ParcelasModal
          passageiroId={passageiro.id}
          onClose={() => setParcelasModalOpen(false)}
          onSuccess={() => {
            // Recarregar dados do passageiro se necessário
            if (onSuccess) onSuccess();
          }}
        />
      )}
    </div>
  );
};

export default PassageiroModal;
